// assets/db-reviews.js
;(function (window, document) {
  // ======= Config =======
const API_BASE     = 'https://gear-db-proxy.onrender.com/reviews';
const PROFILE_BASE = 'https://gear-db-proxy.onrender.com';
  // Use one stable stamp per page-load so identical URLs coalesce
  const TS_SESSION = (window.__REVIEWS_TS ||= Date.now());
  window.__REVIEWS_DIRTY = window.__REVIEWS_DIRTY || new Set();
  
  // ======= Customer helpers (match comments setup) =======
  function getCurrentCustomerId() {
    const id =
      (window.customer && window.customer.id) ||
      window.customerId ||
      window.customerID;
    return id ? String(id).replace(/\D/g, "") : "";
  }
  function getCurrentUserName() {
    return (
      (window.customer && (window.customer.display_name || window.customer.name)) ||
      window.customerName ||
      "Anonymous"
    );
  }

  function ensureReviewReactionStyles() {
  if (document.getElementById('review-reactions-css')) return;
  const css = `
    .review-react-btn .emoji {
      filter: grayscale(100%);
      opacity: .55;
      transition: filter .15s ease, opacity .15s ease;
    }
    .review-react-btn.has-count .emoji,
    .review-react-btn[aria-pressed="true"] .emoji {
      filter: none;
      opacity: 1;
    }
    .review-react-btn .count {
      margin-left: 3px;
    }
  `;
  const style = document.createElement('style');
  style.id = 'review-reactions-css';
  style.textContent = css;
  document.head.appendChild(style);
}
ensureReviewReactionStyles();

  // ======= Small utils =======
  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
  function normalizeProductId(id) {
    const s = String(id || '');
    const m = s.match(/\d+$/);
    return m ? m[0] : s;
  }

  // Log throttler to avoid console spam (once per key per minute)
const __warned = new Map();
function warnOnce(key, ...args) {
  const now  = Date.now();
  const last = __warned.get(key) || 0;
  if (now - last > 60_000) {
    console.warn(...args);
    __warned.set(key, now);
  }
}

  // ---- small helpers for pacing ----
  function sleep(ms)  { return new Promise(r => setTimeout(r, ms)); }
  function jitter(b)  { return b + Math.floor(Math.random() * Math.min(250, b)); }

// Global limiter + cool-off when we see 429s
const _reviewsLimiter = (() => {
  const MAX_CONCURRENCY = 2; // gentler on the proxy
  let active = 0;
  const q = [];
  let cooloffUntil = 0;

  const sleep = (ms) => new Promise(r => setTimeout(r, ms));
  async function waitIfCooling() { while (Date.now() < cooloffUntil) await sleep(120); }
  function setCooloff(ms = 0) {
    const minMs = 2000; // ensure at least 2s cool-off
    const until = Date.now() + Math.max(ms, minMs);
    if (until > cooloffUntil) cooloffUntil = until;
  }
  async function run(fn) {
    await waitIfCooling();
    if (active >= MAX_CONCURRENCY) await new Promise(res => q.push(res));
    active++;
    try { return await fn(); }
    finally { active--; if (q.length) q.shift()(); }
  }
  function isCooling() { return Date.now() < cooloffUntil; }

  return { run, setCooloff, isCooling };
})();

  // Fire a site-wide XP refresh (profile page listens for this)
  function afterXpAward() {
    try {
      window.dispatchEvent(new Event('gd:xp-changed'));
      window.refreshEgStats?.();
      window.refreshEgProfile?.();
      window.refreshEgAchievements?.();
    } catch {}
  }

  async function hydrateReviewsWithProfiles(reviews) {
    if (!Array.isArray(reviews)) return reviews;

    const ids = [...new Set(
      reviews.map(r => String(r.customer_id || r.customerId || '').replace(/\D/g,'')).filter(Boolean)
    )];
    if (!ids.length) return reviews;

    try {
      const res = await fetch(`${PROFILE_BASE}/profiles/bulk?ids=${ids.join(",")}`);
      if (!res.ok) return reviews;
      const profiles = await res.json(); // object map keyed by shopify_customer_id

      return reviews.map(r => {
        const cid  = String(r.customer_id || r.customerId || '').replace(/\D/g,'');
        const prof = profiles[cid];
        if (prof) {
          r.user        = prof.display_name || r.user;
          r.avatar_url  = prof.avatar_url   || r.avatar_url;
          r.profile_url = prof.display_name
            ? `${PROFILE_BASE}/u/${encodeURIComponent(prof.display_name)}`
            : null;
        }
        return r;
      });
    } catch (e) {
      console.warn("[db-reviews] bulk profile fetch failed", e);
      return reviews;
    }
  }

  // ======= API =======
  const _reviewListInflight = new Map(); // coalesces full list loads

  async function loadReviews(productId) {
    const pid = normalizeProductId(productId);
    if (!pid) return [];

    // coalesce full list loads
    if (_reviewListInflight.has(pid)) return _reviewListInflight.get(pid);

    const doFetch = async () => {
      // try /:pid then ?productId=; both behind the limiter + retries
      const tryOnce = async (url) => {
      const res = await fetch(url, { method: 'GET' });
        if (res.status === 429) {
          const ra = res.headers.get('Retry-After');
          const waitMs = ra ? Math.max(0, parseFloat(ra) * 1000) : 1500;
          _reviewsLimiter.setCooloff(waitMs);
          const err = new Error('rate-limited');
          err.code = 429;
          err.waitMs = waitMs;
          throw err;
        }
        if (!res.ok) {
          const err = new Error(`HTTP ${res.status}`);
          err.code = res.status;
          throw err;
        }
        return res.json().catch(() => []);
      };

      const runWithRetry = async (builder) => {
        let attempt = 0;
        const MAX_TRIES = 4;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          try {
            return await _reviewsLimiter.run(() => tryOnce(builder()));
          } catch (e) {
            attempt++;
            if (attempt >= MAX_TRIES) throw e;
            // backoff with jitter, grow a bit faster on 429s
            const base = e && e.code === 429 ? 1200 * attempt : 400 * attempt;
            await sleep(jitter(base));
          }
        }
      };

      // 1) /:pid
      try {
        const bust = window.__REVIEWS_DIRTY?.has(pid) ? `&_t=${Date.now()}` : '';
        const data = await runWithRetry(() =>
          `${API_BASE}/${encodeURIComponent(pid)}?ts=${TS_SESSION}${bust}`
        );
        window.__REVIEWS_DIRTY?.delete(pid);
        return Array.isArray(data) ? data
          : (Array.isArray(data.items) ? data.items : []);
      } catch {
        // 2) fallback ?productId=
        const bust = window.__REVIEWS_DIRTY?.has(pid) ? `&_t=${Date.now()}` : '';
        const data = await runWithRetry(() =>
          `${API_BASE}?productId=${encodeURIComponent(pid)}&ts=${TS_SESSION}${bust}`
        );
        window.__REVIEWS_DIRTY?.delete(pid);
        return Array.isArray(data) ? data
          : (Array.isArray(data.items) ? data.items : []);
      }
    };

    const p = doFetch()
      .catch((err) => {
        warnOnce(`load:${pid}`, '[db-reviews] loadReviews failed', err);
        return [];
      })
      .finally(() => {
        _reviewListInflight.delete(pid);
      });

    _reviewListInflight.set(pid, p);
    return p;
  }

  async function submitReview(productId, starRatings) {
    const pid = normalizeProductId(productId);
    const cid = getCurrentCustomerId();

    if (!pid || !cid) {
      console.error('Review submission failed: Missing productId or customerId', { pid, cid });
      return { ok: false, error: 'Missing productId or customerId' };
    }

    // pull product meta from the wrapper (populated by db-ui.js)
    const wrapper = document.getElementById('quick-review-wrapper');
    const productMeta = wrapper ? {
      title:     wrapper.dataset.productTitle  || '',
      handle:    wrapper.dataset.productHandle || '',
      image_url: wrapper.dataset.productImage  || ''
    } : null;

    const payload = {
      productId: pid,
      customerId: cid,
      user: getCurrentUserName(), // harmless; backend prefers profiles.display_name
       title: document.getElementById('reviewTitle')?.value?.trim().slice(0,300) || null,
      text: document.getElementById('reviewText')?.value || '',
      surface: starRatings.surface || 0,
      base: starRatings.base || 0,
      performance: starRatings.performance || 0,
      comfort: starRatings.comfort || 0,
      durability: starRatings.durability || 0,
      product: productMeta
    };

    const res = await fetch(API_BASE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      console.error('Review submission failed:', data?.error || `HTTP ${res.status}`);
      return { ok: false, error: data?.error || 'Submit failed' };
    }

    afterXpAward(); // ← XP changed, refresh profile/stats/achievements
    return { ok: true };
  }

  // ======= UI helpers =======
  function perReviewAverage(r) {
    const s = Number(r.surface || 0);
    const b = Number(r.base || 0);
    const p = Number(r.performance || 0);
    const c = Number(r.comfort || 0);
    const d = Number(r.durability || 0);
    const vals = [s, b, p, c, d].map(n => (Number.isFinite(n) ? n : 0));
    return vals.reduce((a, n) => a + n, 0) / 5;
  }

  function renderReviewSummary(summaryEl, reviews) {
    if (!summaryEl) return;
    const list = Array.isArray(reviews) ? reviews : [];
    const count = list.length;

    if (!count) {
      summaryEl.innerHTML = `<div class="reviews-summary">No reviews yet.</div>`;
      return;
    }

    // global average across all dimensions, all reviews
    let sum = 0;
    let n   = 0;
    for (const r of list) {
      const s = Number(r.surface || 0);
      const b = Number(r.base || 0);
      const p = Number(r.performance || 0);
      const c = Number(r.comfort || 0);
      const d = Number(r.durability || 0);
      sum += (s + b + p + c + d);
      n   += 5;
    }
    const avg = n ? (sum / n) : 0;
    summaryEl.innerHTML =
      `<div class="reviews-summary"><strong>Average rating:</strong> ${avg.toFixed(1)}★ <span style="color:#888">(${count} review${count>1?'s':''})</span></div>`;
  }

  function computeCategoryAverages(reviews) {
    const keys = ['surface','base','performance','comfort','durability'];
    const out  = { count: Array.isArray(reviews) ? reviews.length : 0, avgs: {} };
    keys.forEach(k => {
      const vals = (reviews || []).map(r => Number(r[k] || 0)).filter(n => Number.isFinite(n));
      out.avgs[k] = vals.length ? (vals.reduce((a,n)=>a+n,0) / vals.length) : 0;
    });
    return out;
  }

  function renderCategoryAverages(rootEl, reviews) {
    const host = rootEl.querySelector('#reviewCategoryAverages');
    if (!host) return;
    const { count, avgs } = computeCategoryAverages(reviews);

    if (!count) {
      host.innerHTML = '';
      return;
    }

    function row(label, key) {
      return `
        <div class="avg-row">
          <span class="avg-label">${label}</span>
          <p class="avg-value">${(avgs[key] || 0).toFixed(1)}<span>★</span></p>
        </div>`;
    }

    host.innerHTML = `
      <div class="reviews-cat-avgs">
        ${row('Surface','surface')}
        ${row('Base','base')}
        ${row('Performance','performance')}
        ${row('Comfort','comfort')}
        ${row('Durability','durability')}
      </div>
    `;
  }

function renderFractionalStars(rating) {
  const n = Math.max(0, Math.min(5, Number(rating) || 0));
  // base row = 5 empty stars
  const emptyStars = '<span class="star">★</span>'.repeat(5);
  // overlay row = 5 filled stars, clipped to rating %
  const pct = (n / 5) * 100;
  const filledStars =
    `<div class="star-overlay" style="width:${pct}%">` +
    '<span class="star">★</span>'.repeat(5) +
    '</div>';

  return `<span class="stars">${emptyStars}${filledStars}</span>`;
}

  function renderStars(n) {
    const v = Math.max(0, Math.min(5, parseInt(n, 10) || 0));
    return '★'.repeat(v) + '☆'.repeat(5 - v);
  }

  function renderReviewList(container, reviews) {
    const list = Array.isArray(reviews) ? reviews : [];
    if (!container) return;

    const html = list.map((r) => {
      const avg = perReviewAverage(r);

      const s = Number(r.surface || 0);
      const b = Number(r.base || 0);
      const p = Number(r.performance || 0);
      const c = Number(r.comfort || 0);
      const d = Number(r.durability || 0);

      const who =
        (r.user && String(r.user).trim()) ||
        (r.user_name && String(r.user_name).trim()) ||
        "Anonymous";
      const txt = (r.text && String(r.text)) || "";

      const avatarUrl = r.avatar_url ? String(r.avatar_url).trim() : "";
      const initials = (who || "")
        .split(/\s+/)
        .map(w => w[0])
        .join("")
        .toUpperCase();


      return `
        <div class="review" data-id="${r.id}" id="review-${r.id}">
          <div class="review-header">
            <div class="review-header-user-rating">
              ${r.profile_url
                ? `<a href="${escapeHtml(r.profile_url)}" target="_blank" rel="noopener"
                      class="review-user-link">
                    ${
                      avatarUrl
                        ? `<img src="${escapeHtml(avatarUrl)}" alt="${escapeHtml(who)}">`
                        : `<div class="avatar-fallback">
                              ${escapeHtml(initials)}
                            </div>`
                    }
                    <strong>${escapeHtml(who)}</strong>
                  </a>`
                : `<div style="display:flex;align-items:center;gap:8px;">
                    ${
                      avatarUrl
                        ? `<img src="${escapeHtml(avatarUrl)}" alt="${escapeHtml(who)}">`
                        : `<div class="avatar-fallback">
                              ${escapeHtml(initials)}
                            </div>`
                    }
                    <strong>${escapeHtml(who)}</strong>
                  </div>`}
              <span class="review-overall">
                ${renderFractionalStars(avg)}
                <span class="rating-number">${avg.toFixed(1)}</span>
              </span>
            </div>
            <div class="review-timestamp">
              ${r.updated_at && r.updated_at !== r.created_at
                ? `Edited on ${new Date(r.updated_at).toLocaleString()}`
                : `Posted on ${new Date(r.created_at).toLocaleString()}`}
            </div>
          </div>
          ${r.title ? `<div class="review-title">${escapeHtml(r.title)}</div>` : ''}
          <div class="review-cats">
            <div class="review-cat">
              <span class="label">Surface</span>
              <span class="value">${renderStars(s)}</span>
            </div>
            <div class="review-cat">
              <span class="label">Base</span>
              <span class="value">${renderStars(b)}</span></span>
            </div>
            <div class="review-cat">
              <span class="label">Performance</span>
              <span class="value">${renderStars(p)}</span>
            </div>
            <div class="review-cat">
              <span class="label">Comfort</span>
              <span class="value">${renderStars(c)}</span>
            </div>
            <div class="review-cat">
              <span class="label">Durability</span>
              <span class="value">${renderStars(d)}</span>
            </div>
          </div>
      ${txt ? `
        <div class="review-text-wrapper">
        <div class="review-text collapsed"
            style="word-break:break-word;"
            data-full="${escapeHtml(txt)}"
            data-short="${escapeHtml(txt.slice(0, 500))}${txt.length > 500 ? '…' : ''}">
          ${escapeHtml(txt.length > 500 ? txt.slice(0, 500) + "…" : txt)}
        </div>
          ${txt.length > 500
            ? `<button class="review-readmore-btn" type="button">Read more</button>`
            : ``}
        </div>` : ``}

          <div class="review-reactions" data-review-id="${r.id}" data-product-id="${r.product_id}">
            <span class="review-reactions-label" style="margin-right:6px;font-size:14px;">
              Find this useful?
            </span>
            ${["👍", "👎"].map(type => {
              const users = (r.reactions && r.reactions[type]) || [];
              const mine  = users.includes(getCurrentCustomerId());
              const count = users.length;
              return `
                <button
                  class="review-react-btn ${count > 0 ? "has-count" : ""}"
                  data-type="${type}"
                  aria-pressed="${mine ? "true" : "false"}"
                >
                  <span class="emoji">${type}</span>
                  ${count > 0 ? `<span class="count">${count}</span>` : ""}
                </button>`;
            }).join("")}
          </div>
        </div>
      `;
    }).join("");

    container.innerHTML = list.length ? html : '';
  }

  function setStars(wrapper, key, val) {
    const cat = wrapper.querySelector(`.rating-category[data-key="${key}"]`);
    if (!cat) return;
    const stars = cat.querySelectorAll('.star');
    stars.forEach((s) => {
      const v = parseInt(s.dataset.value, 10);
      s.classList.toggle('selected', v <= val);
    });
  }

  function attachStarHandlers(wrapper, starRatings) {
    wrapper.querySelectorAll('.rating-category').forEach((cat) => {
      const key = cat.dataset.key; // surface|base|performance|comfort|durability
      const stars = cat.querySelectorAll('.star');

      stars.forEach((star) => {
        const val = parseInt(star.dataset.value, 10);

        star.addEventListener('click', () => {
          starRatings[key] = val;
          stars.forEach((s) => s.classList.toggle('selected', parseInt(s.dataset.value, 10) <= val));
        });

        star.addEventListener('mouseenter', () => {
          stars.forEach((s) => s.classList.toggle('hovered', parseInt(s.dataset.value, 10) <= val));
        });
        star.addEventListener('mouseleave', () => {
          stars.forEach((s) => s.classList.remove('hovered'));
        });
      });
    });
  }

    window.injectReviewSection = function(reviewWrapper, productId) {
      if (!reviewWrapper) return;

    // teardown old review widget
  reviewWrapper.querySelector('#quick-reviews-widget')?.remove();

  // build new wrapper
  const widget = document.createElement('div');
  widget.id = 'quick-reviews-widget';
  widget.className = 'quick-reviews-wrapper';
  widget.innerHTML = `
      <div class="quick-reviews-wrapper">
      <div class="quick-reviews-top">
        <!-- Overall average + per-category averages -->
        <div class="reviewsOverall">
            <div id="reviewAverageBar" class="review-avg"></div>
            <div id="reviewCategoryAverages" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(160px,1fr));gap:6px;"></div>
        </div>
        <!-- CTA bar -->
        <div id="reviewCtaBar">
          ${window.customerLoggedIn ? `
            <button id="openReviewBtn" type="button"
              class="quick-comment-comment-btn"
              style="padding:8px 14px;border-radius:10px;font-weight:600;">
              Write a review
            </button>
            <span id="reviewCtaHint"></span>
          ` : ``}
        </div>
        <!-- Form stays hidden until CTA is clicked -->
            <div id="reviewFormContainer" class="reviewForm hidden" aria-hidden="true" inert>
            <h3 style="text-align: center;margin-bottom: 10px;">Have your say...</h3>
            <div class="rating-group-vertical">
              ${['Surface','Base','Performance','Comfort','Durability'].map(k => `
                <div class="rating-category" data-key="${k.toLowerCase()}">
                  <div class="rating-label">${k}:</div>
                  <div class="stars">
                    ${[1,2,3,4,5].map(n => `<span class="star" data-value="${n}">★</span>`).join('')}
                  </div>
                </div>
              `).join('')}
            </div>
            <!-- NEW: Title input -->
            <input id="reviewTitle" maxlength="300" placeholder="Review title (optional)" class="reviewTitleInput" />
            <textarea id="reviewText" placeholder="(Optional) Add a comment..." class="reviewTextarea"></textarea>
            <div class="edit-review-btns">
              <span id="reviewHint" style="color:#888;font-size:12px;"></span>
              <button id="submitReviewBtn" type="button" class="quick-comment-comment-btn">Submit</button>
              <button id="cancelReviewBtn" type="button" class="quick-comment-comment-btn" style="background:#eee;color:#000;border:1px solid #ccc;">Cancel</button>
            </div>
          </div>
          </div>
        <!-- List BELOW the form -->
        <div id="reviewList"></div>
      </div>
    `;

    reviewWrapper.appendChild(widget);

    if (!window.customerLoggedIn && !document.getElementById("reviewsLoginPrompt")) {
      const notifBlock = document.querySelector(".notification__block");
      if (notifBlock) {
        const loginPrompt = document.createElement("div");
        loginPrompt.id = "reviewsLoginPrompt";
        loginPrompt.innerHTML = `
          <button
            id="openLoginModalBtn"
            type="button"
            class="quick-comment-comment-btn"
            data-login-modal
            data-login-resume='${JSON.stringify({ type: "openProductModal", productId })}'
          >
            Log in to write a review or leave a comment
          </button>
        `;
        notifBlock.insertAdjacentElement("afterend", loginPrompt);

        loginPrompt.querySelector("#openLoginModalBtn")?.addEventListener("click", () => {
          openModal("loginModal");
        });
      }
    }
    // Wire star handlers + expose for later
    const starRatings = {};
    attachStarHandlers(reviewWrapper, starRatings);
    reviewWrapper._starRatings = starRatings;

    // —— CTA toggle helpers (INSIDE injectReviewSection) ——
    const formEl    = reviewWrapper.querySelector('#reviewFormContainer');
    const openBtn   = reviewWrapper.querySelector('#openReviewBtn');
    const cancelBtn = reviewWrapper.querySelector('#cancelReviewBtn');
    const ctaHint   = reviewWrapper.querySelector('#reviewCtaHint');

    function showAccessibly(el) {
      if (!el) return;
      el.classList.remove('hidden');
      el.removeAttribute('aria-hidden');
      el.removeAttribute('inert');
    }

    function hideAccessibly(el) {
      if (!el) return;
      // First prevent focus, then move it out if needed
      el.setAttribute('inert', '');
      if (el.contains(document.activeElement)) {
        try { document.activeElement.blur(); } catch {}
      }
      // Now it’s safe to hide from AT and visually
      el.setAttribute('aria-hidden', 'true');
      el.classList.add('hidden');
    }

    function openForm() {
      if (!window.customerLoggedIn) {
        alert('Please sign in to write a review.');
        return;
      }
      showAccessibly(formEl);

      // hide CTA bar entirely while form is open
      const ctaBar = reviewWrapper.querySelector('#reviewCtaBar');
      if (ctaBar) ctaBar.style.display = 'none';

      const firstFocus =
        formEl.querySelector('.star') ||
        formEl.querySelector('#reviewText') ||
        formEl.querySelector('#submitReviewBtn');
      firstFocus?.focus({ preventScroll: true });
    }

    function closeForm() {
      hideAccessibly(formEl);

      const ctaBar = reviewWrapper.querySelector('#reviewCtaBar');
      if (ctaBar) ctaBar.style.display = '';

      if (openBtn) {
        openBtn.textContent = reviewWrapper._mineReview ? 'Edit your review' : 'Write a review';
      }
    }

    openBtn?.addEventListener('click', () => {
      if (!window.customerLoggedIn) {
        // If you added the global login modal helper, use it:
        if (typeof window.openLoginModal === 'function') {
          window.openLoginModal({ type: 'openProductModal', productId });
        } else {
          // Fallback: redirect to Shopify login and come back to this page
          const here = encodeURIComponent(location.href);
          location.href = `/account/login?return_to=${here}`;
        }
        return;
      }
      const isHidden = formEl.classList.contains('hidden');
      if (isHidden) openForm(); else closeForm();
    });
    cancelBtn?.addEventListener('click', () => {
      openBtn?.focus({ preventScroll: true });
      closeForm();
    });

    const btn = reviewWrapper.querySelector('#submitReviewBtn');
    if (btn) {
      btn.addEventListener('click', async () => {
          // Prevent double-submits and lock focus
          btn.disabled = true;
        // If user already has a review, ask before overwriting it
        if (reviewWrapper._mineReview) {
          const proceed = confirm('You have already posted a review for this product. This will update your existing review. Continue?');
          if (!proceed) return;

          const mine = reviewWrapper._mineReview;
          const sr   = reviewWrapper._starRatings || {};
          const ta   = reviewWrapper.querySelector('#reviewText');
          const newText = (ta?.value || '').trim();
          const newTitle = (reviewWrapper.querySelector('#reviewTitle')?.value || '').trim();

          const unchanged =
            Number(mine.surface || 0)     === Number(sr.surface || 0) &&
            Number(mine.base || 0)        === Number(sr.base || 0) &&
            Number(mine.performance || 0) === Number(sr.performance || 0) &&
            Number(mine.comfort || 0)     === Number(sr.comfort || 0) &&
            Number(mine.durability || 0)  === Number(sr.durability || 0) &&
            String(mine.title || '').trim() === newTitle &&
            String(mine.text || '').trim()  === newText;
          if (unchanged) { alert('No changes to save.'); return; }
        }

        // 🚨 Validation: require all categories rated
        const sr = reviewWrapper._starRatings || {};
        const requiredKeys = ['surface','base','performance','comfort','durability'];
        const missing = requiredKeys.filter(k => !sr[k] || sr[k] < 1);

        if (missing.length > 0) {
          alert("Please rate all categories before submitting your review.");

          const group = reviewWrapper.querySelector('.rating-group-vertical');
          if (group) {
            group.style.outline = '2px solid red';
            group.style.padding = '6px'; // small padding so outline is visible
            group.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => { group.style.outline = ''; }, 2000);
          }

          btn.disabled = false;
          return;
        }

        const { ok } = await submitReview(productId, reviewWrapper._starRatings || {});
          if (!ok) { 
            btn.disabled = false; 
            return;
          }

            // Force the next load of this product's reviews to bypass cache
            window.__REVIEWS_DIRTY?.add(String(normalizeProductId(productId)));

        if (window.renderQuickViewReviews) {
          await window.renderQuickViewReviews(productId, reviewWrapper.closest('#productModalContent'));
        }
         // Move focus out of the form, then close it
        openBtn?.focus({ preventScroll: true });
        closeForm();

        window.refreshEgAchievements?.();
        btn.disabled = false;
      });
    }

  };

  function computeOverallAverage(reviews) {
    if (!Array.isArray(reviews) || reviews.length === 0) return { avg: 0, count: 0 };
    const safe = reviews.map(r => ({
      s: Number(r.surface)      || 0,
      b: Number(r.base)         || 0,
      p: Number(r.performance)  || 0,
      c: Number(r.comfort)      || 0,
      d: Number(r.durability)   || 0,
    }));
    // average each review, then average across reviews
    const perReview = safe.map(v => (v.s + v.b + v.p + v.c + v.d) / 5);
    const avg = perReview.reduce((a, n) => a + n, 0) / perReview.length;
    return { avg, count: reviews.length };
  }

  function renderAverageBar(rootEl, reviews) {
    const bar = rootEl.querySelector('#reviewAverageBar');
    if (!bar) return;
    const { avg, count } = computeOverallAverage(reviews);
    if (!count) {
      bar.innerHTML = '<span class="no-reviews">No reviews yet.</span>';
    } else {
      bar.innerHTML = `
        <div class="avg-rating"><span>${avg.toFixed(1)}</span>
        <span class="avg-star">★</span></div>
        <span class="review-count">(${count} review${count === 1 ? '' : 's'})</span>
      `;
    }
  }

  window.renderQuickViewReviews = async function(productId, rootEl) {
    const reviewWrapper =
      (rootEl && rootEl.querySelector && rootEl.querySelector('#quick-review-wrapper')) ||
      document.getElementById('quick-review-wrapper');
    if (!reviewWrapper) return;

    // Always rebuild widget fresh
    window.injectReviewSection(reviewWrapper, productId);

    // Fetch & render list + averages
    const listEl    = reviewWrapper.querySelector('#reviewList');
    const summaryEl = reviewWrapper.querySelector('#reviewSummary');

    let reviews = await loadReviews(productId);

    // hydrate with current profile info (avatars, latest display_name)
    reviews = await hydrateReviewsWithProfiles(reviews);

    if (summaryEl) renderReviewSummary(summaryEl, reviews);
    renderAverageBar(reviewWrapper, reviews);
    renderCategoryAverages(reviewWrapper, reviews);
    if (listEl) renderReviewList(listEl, reviews);

    // Let the quick-view know reviews are on the page (for deep linking/scroll)
    setTimeout(() => {
      try {
        window.dispatchEvent(
          new CustomEvent('gd:reviews-hydrated', {
            detail: { productId: normalizeProductId(productId), ids: reviews.map(r => r.id) }
          })
        );
      } catch {}
    }, 0);

    // Push fresh avg/count to the table immediately (no extra fetch)
    const { avg, count } = computeOverallAverage(reviews);
    window.DB_REVIEWS?.setLocalSummary?.(normalizeProductId(productId), avg, count);

    // Prefill if user already reviewed (edit mode)
    const cid = getCurrentCustomerId();

    const mine = cid
      ? reviews.find(r => String(r.customer_id || r.customerId || '').replace(/\D/g,'') === cid)
      : null;

    reviewWrapper._mineReview = mine || null;

    // Sync CTA with state
    const openBtn = reviewWrapper.querySelector('#openReviewBtn');
    const ctaHint = reviewWrapper.querySelector('#reviewCtaHint');
    if (!window.customerLoggedIn && openBtn) {
      openBtn.textContent = 'Log in to write a review';
      ctaHint.textContent = '';
    }
    if (openBtn) {
      openBtn.textContent = mine ? 'Edit your review' : 'Write a review';
      ctaHint.textContent = mine ? 'You can update your ratings or text.' : '';
    }

    // Optional deep-link: ?writeReview=1 opens the form on load
    const params = new URLSearchParams(location.search);
    if (params.get('writeReview') === '1') {
      reviewWrapper.querySelector('#openReviewBtn')?.click();
    }

    const form = reviewWrapper.querySelector('#reviewFormContainer');
    const hint = reviewWrapper.querySelector('#reviewHint');
    const btn  = reviewWrapper.querySelector('#submitReviewBtn');

    if (form && window.customerLoggedIn) {
      const sr = (reviewWrapper._starRatings ||= {});

      if (mine) {
        // Prefill stars
        sr.surface     = Number(mine.surface || 0)     || 0;
        sr.base        = Number(mine.base || 0)        || 0;
        sr.performance = Number(mine.performance || 0) || 0;
        sr.comfort     = Number(mine.comfort || 0)     || 0;
        sr.durability  = Number(mine.durability || 0)  || 0;

        // Apply to UI
        const set = (k,v) => {
          const cat = reviewWrapper.querySelector(`.rating-category[data-key="${k}"]`);
          if (!cat) return;
          cat.querySelectorAll('.star').forEach(s => {
            const val = parseInt(s.dataset.value,10);
            s.classList.toggle('selected', val <= v);
          });
        };
        set('surface',     sr.surface);
        set('base',        sr.base);
        set('performance', sr.performance);
        set('comfort',     sr.comfort);
        set('durability',  sr.durability);

        const titleInput = reviewWrapper.querySelector('#reviewTitle');
        if (titleInput) titleInput.value = String(mine.title || '');

        const ta = reviewWrapper.querySelector('#reviewText');
        if (ta) ta.value = String(mine.text || '');

        if (btn)  btn.textContent = 'Save';
        if (hint) hint.textContent = 'Editing your existing review.';
      } else {
        if (btn)  btn.textContent = 'Submit';
        if (hint) hint.textContent = '';
      }
    }
  };

document.addEventListener("click", async (e) => {
  const btn = e.target.closest(".review-react-btn");
  if (!btn) return;

  const wrap      = btn.closest(".review-reactions");
  const reviewId  = wrap?.dataset.reviewId;
  const productId = wrap?.dataset.productId;
  const type      = btn.dataset.type;   // "👍" or "👎"
  const mine      = btn.getAttribute("aria-pressed") === "true";
  const cid       = getCurrentCustomerId();

  if (!cid) {
    alert("Please sign in to react.");
    return;
  }

  btn.disabled = true;
  try {
    const url = `${API_BASE}/${productId}/${reviewId}/reactions`;
    const res = await fetch(url, {
      method: mine ? "DELETE" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ customerId: cid, type }),
    });

    if (!res.ok) {
      console.error("[db-reviews] reaction toggle failed", res.status);
    } else {
      // ✅ update UI immediately

      if (mine) {
        // Removing this reaction
        btn.setAttribute("aria-pressed", "false");
        const countEl = btn.querySelector(".count");
        if (countEl) {
          const newCount = Math.max(0, parseInt(countEl.textContent, 10) - 1);
          if (newCount === 0) {
            countEl.remove();
            btn.classList.remove("has-count");
          } else {
            countEl.textContent = newCount;
          }
        }
      } else {
        // Adding this reaction
        btn.setAttribute("aria-pressed", "true");

        // ✅ un-press opposite immediately
        const opposite = wrap.querySelector(
          `.review-react-btn[data-type="${type === "👍" ? "👎" : "👍"}"]`
        );
        if (opposite && opposite.getAttribute("aria-pressed") === "true") {
          opposite.setAttribute("aria-pressed", "false");
          const oppCount = opposite.querySelector(".count");
          if (oppCount) {
            const newCount = Math.max(0, parseInt(oppCount.textContent, 10) - 1);
            if (newCount === 0) {
              oppCount.remove();
              opposite.classList.remove("has-count");
            } else {
              oppCount.textContent = newCount;
            }
          }
        }

        // increment this one’s count
        let countEl = btn.querySelector(".count");
        if (countEl) {
          countEl.textContent = parseInt(countEl.textContent, 10) + 1;
        } else {
          const span = document.createElement("span");
          span.className = "count";
          span.textContent = "1";
          btn.appendChild(span);
        }
        btn.classList.add("has-count");
      }

      afterXpAward();
      window.refreshEgAchievements?.();
    }
  } catch (err) {
    console.error("[db-reviews] reaction toggle error", err);
  } finally {
    btn.disabled = false;
  }
});

document.addEventListener("click", (e) => {
  const btn = e.target.closest(".review-readmore-btn");
  if (!btn) return;

  const wrap = btn.closest(".review-text-wrapper");
  const textEl = wrap?.querySelector(".review-text");
  if (!textEl) return;

  const isCollapsed = textEl.classList.toggle("collapsed");
  if (isCollapsed) {
    textEl.textContent = textEl.dataset.short;
    btn.textContent = "Read more";
  } else {
    textEl.textContent = textEl.dataset.full;
    btn.textContent = "Read less";
  }
});

 // ======= Prebaked review summaries (single fetch, zero per-row API calls) =======

// Where the single JSON lives (set in theme.liquid; see note below)
const SUMMARY_INDEX_URL =
  (typeof window !== 'undefined' && window.REVIEWS_SUMMARY_URL) ||
  'reviews-summary.v1.json'; // fallback if you hardlink

let _summaryIdx = null;
let _summaryLoadPromise = null;
let _summaryLoadedAt = 0;
const SUMMARY_TTL_MS = 10 * 60 * 1000; // re-fetch occasionally just in case

async function loadSummaryIndexOnce() {
  if (_summaryIdx && (Date.now() - _summaryLoadedAt) < SUMMARY_TTL_MS) return _summaryIdx;
  if (_summaryLoadPromise) return _summaryLoadPromise;

  _summaryLoadPromise = (async () => {
    try {
      const res = await fetch(SUMMARY_INDEX_URL, { method: 'GET', credentials: 'omit' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      _summaryIdx = (json && (json.items || json)) || {};
      _summaryLoadedAt = Date.now();

      // Let sidebar/listeners know at least one round of summaries exists
      if (!window.__reviewsReady) {
        window.__reviewsReady = true;
        try { window.dispatchEvent(new Event('db:reviews-hydrated')); } catch {}
      }
      return _summaryIdx;
    } catch (e) {
      console.warn('[db-reviews] summary index load failed', e);
      _summaryIdx = _summaryIdx || {};
      return _summaryIdx;
    } finally {
      _summaryLoadPromise = null;
    }
  })();

  return _summaryLoadPromise;
}

// Bucket helper (shared by cell painter / sidebar)
function bucketFor(avg, count) {
  if (!count) return 'No reviews';
  return (avg >= 4.5) ? '4.5★+' :
         (avg >= 4.0) ? '4★+'   :
         (avg >= 3.5) ? '3.5★+' :
         (avg >= 3.0) ? '3★+'   : '<3★';
}

window.DB_REVIEWS = window.DB_REVIEWS || {};
window.DB_REVIEWS.bucketFor = bucketFor;

/** Returns { avg, count } for a given productId from the single JSON. */
window.DB_REVIEWS.getSummary = async function(productId) {
  const pid = normalizeProductId(productId);
  if (!pid) return { avg: 0, count: 0 };
  const idx = await loadSummaryIndexOnce();
  const e = idx[pid];
  return e ? { avg: Number(e.avg) || 0, count: Number(e.count) || 0 }
           : { avg: 0, count: 0 };
};

window.DB_REVIEWS.setLocalSummary = function(productId, avg, count) {
  const pid = normalizeProductId(productId);
  if (!pid) return;

  // keep the in-memory index hot
  _summaryIdx = _summaryIdx || {};
  _summaryIdx[pid] = { avg, count };
  (window.__reviewCache ||= new Map()).set(pid, { avg, count });

  // repaint any visible table cells for this PID
  document.querySelectorAll(`.db-reviews[data-pid="${pid}"]`).forEach((el) => {
    const wrap = el.querySelector('.td-wrapper');
    if (!wrap) return;
    const a1 = (avg || 0).toFixed(1);
    const a2 = (avg || 0).toFixed(2);
    const has = (count || 0) > 0 && (avg || 0) > 0;

    wrap.innerHTML = `
      <span class="review-text">${a1}</span>
      <span class="review-star" style="color:${has ? '#f5b50a' : '#000'};">★</span>
      <span class="review-count">(${count || 0})</span>
    `;
    wrap.dataset.value  = a2;
    wrap.dataset.count  = String(count || 0);
    wrap.dataset.filter =
      (count || 0) === 0 ? "No reviews" :
      avg >= 4.5 ? "4.5★+" :
      avg >= 4.0 ? "4★+"   :
      avg >= 3.5 ? "3.5★+" :
      avg >= 3.0 ? "3★+"   : "<3★";
    el.title = `${a1} out of 5 stars based on ${count || 0} review${(count||0)===1?'':'s'}`;
    el.setAttribute('aria-label', el.title);
    el.dataset.revLoaded = '1';
  });
};

  // Simple star text for table cells (rounded to nearest whole star)
  window.DB_REVIEWS.renderStars = function(avg) {
    const n = Math.round(Math.max(0, Math.min(5, Number(avg) || 0)));
    return '★'.repeat(n) + '☆'.repeat(5 - n);
  };

  // announce that DB_REVIEWS is ready (lets hydrateDbReviews proceed if it was waiting)
  if (!window.__reviewsApiReady) {
    window.__reviewsApiReady = true;
    try { window.dispatchEvent(new Event('gd:reviews-api-ready')); } catch {}
  }

})(window, document);
